import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Headless behavior for a menu-button style dropdown (WAI-ARIA "menu button"
 * pattern: https://www.w3.org/WAI/ARIA/apg/patterns/menu-button/).
 *
 * Handles open/close state, click-outside dismissal, and roving keyboard
 * focus across the menu items (ArrowUp/ArrowDown/Home/End/Escape), and
 * returns focus to the trigger when the menu closes.
 */
export const useDropdownMenu = (itemCount: number) => {
    const [isOpen, setIsOpen] = useState(false);
    const [activeIndex, setActiveIndex] = useState(-1);
    const containerRef = useRef<HTMLElement>(null);
    const triggerRef = useRef<HTMLElement>(null);
    const itemRefs = useRef<(HTMLElement | null)[]>([]);

    const close = useCallback((focusTrigger = false) => {
        setIsOpen(false);
        setActiveIndex(-1);
        if (focusTrigger) {
            const trigger = triggerRef.current;
            trigger?.focus();
            // Some triggers (e.g. a non-focusable wrapper div around a
            // custom button) can't take focus directly - fall back to the
            // first focusable descendant so focus doesn't get lost to <body>.
            if (trigger && document.activeElement !== trigger) {
                trigger
                    .querySelector<HTMLElement>(
                        'button, a[href], input, select, textarea, [tabindex]'
                    )
                    ?.focus();
            }
        }
    }, []);

    const open = useCallback((focusIndex = 0) => {
        setIsOpen(true);
        setActiveIndex(focusIndex);
    }, []);

    const toggle = useCallback(() => {
        setIsOpen((prev) => {
            const next = !prev;
            setActiveIndex(next ? 0 : -1);
            return next;
        });
    }, []);

    // rootClose: dismiss when clicking/tapping outside the trigger+menu.
    // A plain containment check (rather than the shared useClickOutside
    // z-index heuristic) so this is reliable regardless of the stacking
    // context the dropdown happens to render in.
    useEffect(() => {
        if (!isOpen) return;
        const onPointerDown = (e: MouseEvent) => {
            const target = e.target as Node;
            if (!containerRef.current?.contains(target)) {
                close(false);
            }
        };
        document.addEventListener("mousedown", onPointerDown);
        return () => document.removeEventListener("mousedown", onPointerDown);
    }, [isOpen, close]);

    useEffect(() => {
        if (isOpen && activeIndex >= 0) {
            itemRefs.current[activeIndex]?.focus();
        }
    }, [isOpen, activeIndex]);

    const onTriggerKeyDown = useCallback(
        (e: React.KeyboardEvent) => {
            if (
                e.key === "ArrowDown" ||
                e.key === "Enter" ||
                e.key === " "
            ) {
                e.preventDefault();
                open(0);
            } else if (e.key === "ArrowUp") {
                e.preventDefault();
                open(itemCount - 1);
            } else if (e.key === "Escape" && isOpen) {
                e.preventDefault();
                close(true);
            }
        },
        [open, close, isOpen, itemCount]
    );

    const onMenuKeyDown = useCallback(
        (e: React.KeyboardEvent) => {
            switch (e.key) {
                case "ArrowDown":
                    e.preventDefault();
                    setActiveIndex((i) => (i + 1) % itemCount);
                    break;
                case "ArrowUp":
                    e.preventDefault();
                    setActiveIndex((i) => (i - 1 + itemCount) % itemCount);
                    break;
                case "Home":
                    e.preventDefault();
                    setActiveIndex(0);
                    break;
                case "End":
                    e.preventDefault();
                    setActiveIndex(itemCount - 1);
                    break;
                case "Escape":
                    e.preventDefault();
                    close(true);
                    break;
                case "Tab":
                    close(false);
                    break;
                default:
                    break;
            }
        },
        [itemCount, close]
    );

    return {
        isOpen,
        activeIndex,
        open,
        close,
        toggle,
        containerRef,
        triggerRef,
        itemRefs,
        onTriggerKeyDown,
        onMenuKeyDown
    };
};
